import { ReportInlinePhotoGrid } from './ReportInlinePhotoGrid';
import type { ReportInlinePhotoGridPhoto } from './ReportInlinePhotoGrid';
import { OptionChip } from '../../components/ui/Chip';
import { resolveTableColumnsView, computeTableTotalsRow } from '../../lib/agentShared/formSchema';
import type { TableColumnConfig, TableColumnEmphasis, TableCellOverflow } from '../../lib/agentShared/formSchema';

// ------------------------------------------------------------------
// ReportDataTable — a report's `kind: 'table'` section, as one real table
// (story 4-81; P06 §4). Story 4-118 (Table Tools) added column select/
// reorder/emphasis, wrap-vs-truncate and a totals row — see this file's own
// header comment further down for each mechanism.
//
// WHY A NEW PRIMITIVE AND NOT THE EXISTING `Table`. `Table` (components/ui/
// Table.tsx) wraps its rows in `glass-card` — the app-shell's translucent,
// blurred screen surface. `ReportRender.tsx`'s own header comment documents
// a hard constraint this report renderer was built to satisfy: "one design,
// consistent web/print/PDF" (story 4-78's `a86b338` fidelity fix) — the
// print stylesheet flattens `.report-render` itself to plain white ink, but
// a NESTED `.glass-card` table would keep its own blur/translucency and
// print as a floating glassy box on an otherwise flat page. This primitive
// is the print-safe table: a plain bordered box, semantic tokens only, no
// backdrop-filter — the same "flat is the target" rule `ReportScoreStrip`/
// `ReportFindingsRollup` already follow.
//
// a11y: a real `<table>` with `scope="col"` headers and an optional
// `caption` (visually hidden — the section's own visible heading already
// names it on screen; the caption is for a screen-reader user who jumps
// straight to the table via table-navigation and never passed the heading).
// An empty TEXT cell renders '—', the same "no value" convention the
// report's `<dl>` rows and the PDF's row grid already use, rather than a
// blank cell a screen reader announces as nothing at all. A truncated cell
// (story 4-118) keeps a `title` attribute carrying its full value — an
// additional, non-load-bearing affordance (the full text is ALREADY present
// in the cell's own markup either way, see below) that lets a sighted mouse
// user recover it without a screen reader.
//
// ── Cell kind dispatch (story 4-83c, 4-86) ──────────────────────────────
// A cell was a bare `string` until story 4-83c — a photo-type column's cell
// had nowhere to carry a thumbnail, so it stringified the evidence-id array
// (`contracts/reports.js#ReportTableCell`'s own header comment has the full
// bug history). `CELL_KIND_RENDERERS` is the render-side lookup table that
// story's forward-looking constraint asked for: keyed by the SAME `kind`
// vocabulary the wire carries (`'text'` | `'photo-grid'` | `'chip'`), read
// here instead of an inline `if (cell.kind === 'photo-grid')` — so a future
// cell kind (video/audio, the implementer-scoped checkbox variant) is one
// new entry, not a rewrite of this component or its caller.
// `pdfExport.service.js#ROW_KIND_RENDERERS` is this table's backend PDF
// twin — same three keys, same three behaviors, hand-maintained per this
// pipeline's existing pattern. A bare `string` cell (a legacy pre-4-83c
// `tableRows` entry surviving inside a pinned `version.assembledContent`
// snapshot — frozen JSON, never rewritten in place) is treated as legacy
// text rather than faulted on. `chip` (story 4-86) reuses `OptionChip`
// (`components/ui/Chip.tsx`) — the same tinted-chip look a select field's
// option already renders everywhere else an option value is shown.
export type ReportDataTableCell =
  | string
  | {
      kind: 'text' | 'photo-grid' | 'chip';
      value: string;
      photos: ReportInlinePhotoGridPhoto[] | null;
      chip: { label: string; color: string | null } | null;
    };

const CELL_KIND_RENDERERS: Record<'text' | 'photo-grid' | 'chip', (cell: Exclude<ReportDataTableCell, string>) => JSX.Element> = {
  text: (cell) => <>{cell.value || '—'}</>,
  'photo-grid': (cell) => <ReportInlinePhotoGrid photos={cell.photos ?? []} />,
  chip: (cell) =>
    cell.chip ? (
      <OptionChip
        option={cell.chip.color ? { label: cell.chip.label, color: cell.chip.color } : { label: cell.chip.label }}
      />
    ) : (
      <>{cell.value || '—'}</>
    ),
};

const cellText = (cell: ReportDataTableCell): string => (typeof cell === 'string' ? cell : cell.value);
const cellKind = (cell: ReportDataTableCell): 'text' | 'photo-grid' | 'chip' => (typeof cell === 'string' ? 'text' : cell.kind);

// Story 4-118 — `overflow:'truncate'` scoped to TEXT/legacy-string cells
// only (a photo-grid's thumbnails and a chip's pill are already compact,
// visual content, not the "long free-text value" this control targets). A
// SINGLE-LINE clip via Tailwind's `truncate` utility (`overflow:hidden;
// text-overflow:ellipsis; white-space:nowrap`) — the FULL value stays the
// actual text node inside the `<span>` (never shortened/sliced in
// JavaScript), so it is present in the DOM for any accessible-text reader
// and in the PDF's extractable text layer exactly as `pdftotext` verified
// live for this story (see `docs/prd/17-story-map.md`'s own `4-118` DONE
// annotation for the real generated-PDF result) — only the PAINT is
// visually clipped, never the characters themselves.
function renderCell(cell: ReportDataTableCell, overflow: TableCellOverflow): JSX.Element {
  const kind = cellKind(cell);
  const rendered =
    typeof cell === 'string'
      ? cell || '—'
      : (CELL_KIND_RENDERERS[kind] ?? CELL_KIND_RENDERERS.text)(cell);
  if (overflow === 'truncate' && kind === 'text') {
    const text = cellText(cell);
    return (
      <span className="block truncate" title={text || undefined}>
        {rendered}
      </span>
    );
  }
  return <>{rendered}</>;
}

// Story 4-118 — a named emphasis LEVEL, not a pixel width: each maps to a
// proportional weight (`table-layout:fixed`'s `<th>` widths are computed
// from these, never a free-form CSS size an author could type in). 'primary'
// wider, 'secondary' the SAME width as the untouched default ('standard') —
// the two are kept as distinct stored values because 'secondary' is a real
// authored CHOICE (including the automatic 'compact'->'secondary' downgrade
// below), not merely "never configured", even though they render identically
// today.
const EMPHASIS_WEIGHT: Record<TableColumnEmphasis, number> = {
  standard: 1,
  secondary: 1,
  primary: 2,
  compact: 0.5,
};

export interface ReportDataTableProps {
  columns: string[];
  /** Story 4-118 — stable `field.key` per column, same order/length as
   *  `columns`; `undefined`/`null`/mismatched falls back to natural
   *  rendering (see `resolveTableColumnsView`'s own comment). */
  columnKeys?: string[] | null;
  rows: ReportDataTableCell[][];
  /** Story 4-118 — `section.presentation.tableColumns` (select/reorder/
   *  emphasis); `undefined`/`null` renders every column, natural order,
   *  standard emphasis — today's unchanged behavior. */
  tableColumns?: TableColumnConfig[] | null;
  /** Story 4-118 — `section.presentation.overflow`; default `'wrap'`. */
  overflow?: TableCellOverflow;
  /** Story 4-118 — `section.presentation.showTotalsRow`; default `false`. */
  showTotalsRow?: boolean;
  /** Visually hidden — supplements the section's own visible heading for table-navigation a11y. */
  caption?: string;
  className?: string;
  /**
   * Story 4-120 — `ReportStyle.borderDensity`. `'standard'` (default) is the
   * pre-4-120 hairline `border-border/60` unchanged; `'bold'` thickens the
   * SAME outer border and header rule to `border-2`.
   */
  borderWeight?: 'standard' | 'bold';
  /**
   * Story 4-120 — `ReportStyle.variants.table`. `'standard'` (default) is
   * the existing flat `divide-y` rows unchanged; `'striped'` tints every
   * other body row with a subtle tone (`stripeColor`-resolved at the call
   * site — `null` falls back to the token-driven `bg-muted/30` default, a
   * caller-supplied hex is the `colorRoles.border: 'brand'` override).
   */
  striped?: boolean;
  /** Story 4-120 — the resolved stripe tint for `striped` above; `null` uses the token default. */
  stripeColor?: string | null;
}

// Story 4-116 — `section.presentation.repeatHeading`'s real print effect
// (`reportPrint.css`'s `.report-section--repeat-heading` rule) is applied by
// the CALLER on the wrapping `<section>`, not this component: a plain CSS
// descendant selector (`.report-section--repeat-heading thead`) reaches this
// table's own `<thead>` without this component needing to know anything
// about page fragmentation at all.
export function ReportDataTable({
  columns,
  columnKeys = null,
  rows,
  tableColumns = null,
  overflow = 'wrap',
  showTotalsRow = false,
  caption,
  className = '',
  borderWeight = 'standard',
  striped = false,
  stripeColor = null,
}: ReportDataTableProps): JSX.Element {
  // Story 4-118 — the ONE resolver (`agents/shared/formSchema.js`) both this
  // component and `pdfExport.service.js#renderDataTable` call through, so
  // "select/reorder/emphasis renders the same in Preview and PDF" is
  // enforced by shared code, not by two hand-synced implementations
  // agreeing by discipline alone. Untouched (no `tableColumns` stored) ->
  // `resolveTableColumnsView` returns the exact natural-order shape this
  // component always rendered pre-4-118, so a plain `<table
  // class="w-full text-left text-sm">` with no width styling is still what
  // an unconfigured table renders — byte-identical markup for every
  // existing `4-83c` table fixture.
  // `resolveTableColumnsView` is generic pure JS shared with the PDF
  // renderer (`.d.ts` types its rows `unknown[][]`) — this call site is the
  // one place that knows the real element type, so it is cast back once
  // here rather than threading a generic through the shared declaration.
  const rawView = resolveTableColumnsView(columns, columnKeys ?? null, rows, tableColumns);
  const view = rawView as unknown as { columns: typeof rawView.columns; rows: ReportDataTableCell[][] };
  const totals = showTotalsRow ? computeTableTotalsRow(view.rows) : null;
  const usesCustomWidths = overflow === 'truncate' || view.columns.some((c) => c.emphasis !== 'standard');
  const totalWeight = view.columns.reduce((sum, c) => sum + EMPHASIS_WEIGHT[c.emphasis], 0) || 1;

  const borderClass = borderWeight === 'bold' ? 'border-2' : 'border';
  const headerBorderClass = borderWeight === 'bold' ? 'border-b-2' : 'border-b';
  return (
    <div className={`overflow-x-auto rounded-lg ${borderClass} border-border/60 ${className}`}>
      <table className={`w-full text-left text-sm${usesCustomWidths ? ' table-fixed' : ''}`}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className={`${headerBorderClass} border-border/60 bg-muted/40`}>
          <tr>
            {view.columns.map((col, index) => (
              <th
                key={`${col.label}-${index}`}
                scope="col"
                style={usesCustomWidths ? { width: `${(EMPHASIS_WEIGHT[col.emphasis] / totalWeight) * 100}%` } : undefined}
                className="px-3 py-2 text-label font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {view.rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={striped && rowIndex % 2 === 1 ? (stripeColor ? '' : 'bg-muted/30') : undefined}
              style={striped && rowIndex % 2 === 1 && stripeColor ? { backgroundColor: `${stripeColor}14` } : undefined}
            >
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 align-top text-foreground">
                  {renderCell(cell, overflow)}
                </td>
              ))}
            </tr>
          ))}
          {/* Story 4-118 — `showTotalsRow`'s one aggregate row, visually
              distinguished (bold, tinted background) from a real data row so
              a reader never mistakes it for another repeat instance. */}
          {totals && (
            <tr className="bg-muted/30 font-semibold">
              {totals.map((cell, index) => (
                <td key={index} className="px-3 py-2 align-top text-foreground">
                  {cell}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
